import { z } from 'zod';

export const RoleSchema = z.enum(['employee', 'division_head', 'bu_head', 'cfo', 'admin', 'board']);
/** Roles a person can hold on top of being staff; heads come from the org chart. */
export const SystemRoleSchema = z.enum(['admin', 'cfo', 'board']);
export type SystemRole = z.infer<typeof SystemRoleSchema>;

export type Capabilities = {
  admin: boolean; finance: boolean; board: boolean; submitsAllocation: boolean;
  approvesDivisions: number[]; approvesUnits: number[]; forecastUnits: number[];
  companyReports: boolean; salaryDetail: boolean;
};
export type Role = z.infer<typeof RoleSchema>;

export const AllocationStatusSchema = z.enum(['draft', 'submitted', 'div_approved', 'approved', 'rejected']);
export type AllocationStatus = z.infer<typeof AllocationStatusSchema>;

export const LoginSchema = z.object({ username: z.string().trim().min(1).max(150), password: z.string().min(1).max(256) });
export const ChangePasswordSchema = z.object({ currentPassword: z.string().min(1), newPassword: z.string().min(8).max(128) });

export const UserSchema = z.object({
  id: z.number().int().positive(), username: z.string(), nameFa: z.string(), role: RoleSchema,
  divisionId: z.number().int().positive().nullable(), isActive: z.boolean(), isSuperuser: z.boolean(),
  forcePasswordChange: z.boolean(), isDivisionHead: z.boolean(), isBuHead: z.boolean(),
  profileImageUrl: z.string().default(''),
});
export type User = z.infer<typeof UserSchema> & { roles: SystemRole[]; capabilities: Capabilities };

export const AllocationLineInputSchema = z.object({ businessUnitId: z.number().int().positive(), percentage: z.string().regex(/^\d+(\.\d{1,4})?$/) });
export const AllocationInputSchema = z.object({ notes: z.string().max(4000).default(''), lines: z.array(AllocationLineInputSchema).max(100) });
export const AllocationActionSchema = z.object({ reason: z.string().trim().max(4000).optional() });

export const SalaryRowSchema = z.object({ employeeId: z.number().int().positive(), amountRial: z.string().regex(/^\d+$/), hours: z.number().int().min(0).max(744) });
export const SalaryMonthInputSchema = z.object({ rows: z.array(SalaryRowSchema) });

export const UsernameSchema = z.string().trim().toLowerCase().min(3).max(40).regex(/^[a-z][a-z0-9._-]*$/, 'نام کاربری فقط حروف انگلیسی، عدد، نقطه، خط تیره و زیرخط');

export const AdminUserInputSchema = z.object({
  username: UsernameSchema, nameFa: z.string().trim().min(1).max(200),
  email: z.string().email().or(z.literal('')).default(''), role: RoleSchema.default('employee'),
  divisionId: z.number().int().positive().nullable().default(null), isActive: z.boolean().default(true),
  roles: z.array(SystemRoleSchema).max(3).default([]),
});
export const AdminUserCreateSchema = AdminUserInputSchema.extend({
  password: z.string().min(8).max(128).optional().or(z.literal('').transform(() => undefined)),
});
export const ReissueCredentialsSchema = z.object({
  users: z.array(z.object({ id: z.number().int().positive(), username: UsernameSchema })).min(1).max(1000),
});

export const ReportingEntitySchema = z.enum(['pishgam', 'faraz']);
export type ReportingEntity = z.infer<typeof ReportingEntitySchema>;
export const REPORTING_ENTITY_LABELS: Record<ReportingEntity, string> = { pishgam: 'پیشگام', faraz: 'پایش سرمایه فراز' };

export const FpaLineKindSchema = z.enum(['revenue', 'cost', 'kpi', 'assumption']);
export const FpaScenarioSchema = z.enum(['actual', 'forecast']);
export const FpaEntryCellSchema = z.object({
  lineDefId: z.number().int().positive(), year: z.number().int().min(1300).max(1600), month: z.number().int().min(1).max(12),
  scenario: FpaScenarioSchema,
  // Money lines: million rial as entered on screen. KPI lines: the raw value. null clears the cell.
  value: z.string().regex(/^\d+(\.\d{1,4})?$/).nullable(),
});
export const FpaEntrySaveSchema = z.object({
  fiscalYear: z.number().int().min(1300).max(1600), round: z.number().int().min(1).max(4),
  cells: z.array(FpaEntryCellSchema).min(1).max(5000),
});
export const FpaLineDefInputSchema = z.object({
  businessUnitId: z.number().int().positive(), labelFa: z.string().trim().min(1).max(200),
  kind: FpaLineKindSchema, isPersonnel: z.boolean().default(false), isActive: z.boolean().default(true),
  sortOrder: z.number().int().min(0).max(100000).optional(),
});
export const FpaPlanCreateSchema = z.object({
  fiscalYear: z.number().int().min(1300).max(1600), round: z.number().int().min(1).max(4),
  copyFromRound: z.number().int().min(1).max(4).optional(), setActive: z.boolean().default(true),
});
export const OrganizationInputSchema = z.object({
  nameFa: z.string().trim().min(1).max(200), section: z.enum(['fs', 'inv']),
  headId: z.number().int().positive().nullable(), isActive: z.boolean(), sortOrder: z.number().int().min(0).max(32767).optional(),
  isCostBucket: z.boolean().optional(), reportingEntity: ReportingEntitySchema.optional(),
});

export type ApiError = { statusCode: number; code: string; message: string; details?: unknown };
export type Money = string;
export type Percentage = string;
